import { useEffect, useRef, useState, type ClipboardEvent, type FormEvent } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/modules/auth/hooks/useAuth';
import { getPostLoginRoute } from '@/modules/auth/lib/userMode';
import {
  clearPendingMfaChallenge,
  getPendingMfaChallenge,
  getPendingMfaChallengeRemainingMs,
} from '@/modules/auth/lib/pendingMfaChallenge';
import { isApiError } from '@/shared/lib/apiClient';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';

export const Route = createFileRoute('/auth/mfa-verify')({
  component: MfaVerifyPage,
});

function MfaVerifyPage() {
  const { t } = useTranslation();
  const { completeMfaLogin } = useAuth();
  const navigate = useNavigate();
  const totpInputRef = useRef<HTMLInputElement>(null);

  const [useRecovery, setUseRecovery] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!getPendingMfaChallenge()) {
      void navigate({ to: '/auth/login', replace: true });
      return;
    }
    totpInputRef.current?.focus();
  }, [navigate]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (getPendingMfaChallengeRemainingMs() <= 0) {
        clearPendingMfaChallenge();
        void navigate({ to: '/auth/login', replace: true });
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [navigate]);

  const handleCancel = () => {
    clearPendingMfaChallenge();
    void navigate({ to: '/auth/login' });
  };

  const handleTotpChange = (value: string) => {
    setCode(value.replace(/\D/g, '').slice(0, 6));
    setError(null);
  };

  const handleTotpPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (digits.length > 0) {
      e.preventDefault();
      setCode(digits);
      setError(null);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const user = await completeMfaLogin(code.trim());
      await navigate({ to: getPostLoginRoute(user) });
    } catch (err) {
      if (isApiError(err) && (err.status === 400 || err.status === 401)) {
        setError(t('auth.mfaVerify.invalidCode'));
      } else if (err instanceof Error && err.message.includes('expired')) {
        setError(t('auth.mfaVerify.sessionExpired'));
        clearPendingMfaChallenge();
      } else {
        setError(t('auth.errors.generic'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('auth.mfaVerify.title')}</CardTitle>
        <CardDescription>
          {useRecovery ? t('auth.mfaVerify.recoverySubtitle') : t('auth.mfaVerify.subtitle')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-1.5">
            {useRecovery ? (
              <>
                <Label htmlFor="recovery-code">{t('auth.mfaVerify.recoveryLabel')}</Label>
                <Input
                  id="recovery-code"
                  type="text"
                  autoComplete="off"
                  placeholder="ABCD-EFGH"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    setError(null);
                  }}
                  className="font-mono"
                />
              </>
            ) : (
              <>
                <Label htmlFor="mfa-code">{t('auth.mfaVerify.codeLabel')}</Label>
                <Input
                  ref={totpInputRef}
                  id="mfa-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => handleTotpChange(e.target.value)}
                  onPaste={handleTotpPaste}
                  className="text-center text-lg tracking-widest font-mono"
                />
              </>
            )}
          </div>

          <Button
            type="button"
            variant="ghost"
            className="w-full text-sm"
            onClick={() => {
              setUseRecovery((v) => !v);
              setCode('');
              setError(null);
            }}
          >
            {useRecovery ? t('auth.mfaVerify.useTotp') : t('auth.mfaVerify.useRecovery')}
          </Button>

          <Button type="submit" className="w-full" isLoading={isSubmitting} disabled={!code.trim()}>
            {isSubmitting ? t('auth.mfaVerify.submitting') : t('auth.mfaVerify.submit')}
          </Button>

          <p className="text-center text-sm">
            <button type="button" className="text-primary hover:underline" onClick={handleCancel}>
              {t('auth.mfaVerify.backToLogin')}
            </button>
          </p>
        </form>
      </CardContent>
    </Card>
  );
}
